import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Row, Col, DatePicker, message, Divider } from "antd";
import dayjs from "dayjs";
import * as participantesService from "../../../services/participantes/participantesService";
import * as ubicacionesService from "../../../services/participantes/ubicacionesService";
import "./ParticipantModal.css";

const { Option } = Select;

export function ParticipantModal({ open, onCancel, participant, mode, onSaved, userInfo, maestros }) {

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [municipios, setMunicipios] = useState([]);
  const [loadingMunicipios, setLoadingMunicipios] = useState(false);

  const findIdByCodigo = (maestroArray, codigo) => {
    if (!maestroArray || !codigo) return null;
    const item = maestroArray.find(item => item.codigo === codigo);
    return item ? item.id : null;
  };

  useEffect(() => {
    if (!open) return;
    const loadMunicipios = async () => {
      if ((isEditMode || isViewMode) && participant?.departamento_id) {
        setLoadingMunicipios(true);
        try {
          const munRes = await ubicacionesService.getMunicipios(participant.departamento_id);
          setMunicipios(munRes.data || []);
        } catch (error) {
          console.error("Error cargando municipios:", error);
          setMunicipios([]);
        } finally {
          setLoadingMunicipios(false);
        }
      } else {
        setMunicipios([]);
      }
    };

    loadMunicipios();
  }, [open, participant, isEditMode, isViewMode]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setMunicipios([]);
      return;
    }

    if (!participant) {
      if (isCreateMode) {
        form.setFieldsValue({
          estado_id: 1, 
          tipo_doc_id: 1, 
          genero_id: 1 
        });
      }
      return;
    }

    const tipoDocId = participant.tipo_documento_id ||
      findIdByCodigo(maestros.tiposDocumento, participant.tipo_documento);

    const generoId = participant.genero_id ||
      findIdByCodigo(maestros.generos, participant.genero);

    const estadoId = participant.estado_participante_id ||
      findIdByCodigo(maestros.estadosParticipante, participant.estado_participante) ||
      1; 

    form.setFieldsValue({
      nombres: participant.nombres,
      apellidos: participant.apellidos,
      tipo_doc_id: tipoDocId,
      num_identificacion: participant.num_identificacion,
      genero_id: generoId,
      fecha_nacimiento: participant.fecha_nacimiento ? dayjs(participant.fecha_nacimiento) : null,
      telefono: participant.telefono || "",
      correo: participant.correo || "",
      departamento_id: participant.departamento_id,
      municipio_id: participant.municipio_id,
      estado_id: estadoId
    });

  }, [open, participant, form, isCreateMode, maestros]);

  const handleDepartamentoChange = async (departamentoId) => {
    if (!departamentoId) {
      setMunicipios([]);
      form.setFieldsValue({ municipio_id: null });
      return;
    }

    setLoadingMunicipios(true);
    try {
      const res = await ubicacionesService.getMunicipios(departamentoId);
      setMunicipios(res.data || []);
      form.setFieldsValue({ municipio_id: null }); 
    } catch (error) {
      console.error("Error cargando municipios:", error);
      message.error("Error cargando municipios");
      setMunicipios([]);
    } finally {
      setLoadingMunicipios(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        fecha_nacimiento: values.fecha_nacimiento
          ? values.fecha_nacimiento.format('YYYY-MM-DD')
          : null
      };
      if (isCreateMode) {
        await participantesService.createParticipante(formattedValues, userInfo);
        message.success("Participante creado correctamente");
      } else {
        await participantesService.updateParticipante(
          participant.id,
          formattedValues,
          userInfo
        );
        message.success("Participante actualizado correctamente");
      }
      onSaved(true, participant?.id);
      onCancel();
    } catch (error) {
      console.error("Error guardando participante:", error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Error al guardar los datos";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderViewField = (label, value) => (
    <div className="view-field">
      <label className="view-label">{label}:</label>
      <span className="view-value">{value || "No especificado"}</span>
    </div>
  );

  return (
    <Modal
      title={
        <span style={{ fontSize: 18, fontWeight: "bold", color: "#042956" }}>
          {isCreateMode ? "Nuevo Participante" : isEditMode ? "Editar Participante" : "Detalles del Participante"}
        </span>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={700}
      className="participant-modal"
      destroyOnHidden={true}
    >
      {isViewMode ? (
        <div className="view-mode">
          <Divider orientation="left" style={{ marginTop: 0 }}>
            Información Personal
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              {renderViewField("Nombres", participant?.nombres)}
            </Col>
            <Col span={12}>
              {renderViewField("Apellidos", participant?.apellidos)}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              {renderViewField(
                "Tipo Documento",
                participant?.tipo_documento_nombre || participant?.tipo_documento
              )}
            </Col>
            <Col span={8}>
              {renderViewField(
                "Número Identificación",
                participant?.num_identificacion
              )}
            </Col>
            <Col span={8}>
              {renderViewField(
                "Género",
                participant?.genero_nombre || participant?.genero
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              {renderViewField(
                "Fecha Nacimiento",
                participant?.fecha_nacimiento_formateada || participant?.fecha_nacimiento
              )}
            </Col>
            <Col span={12}>
              {renderViewField("Teléfono", participant?.telefono)}
            </Col>
          </Row>
          <Divider orientation="left">Ubicación</Divider>
          <Row gutter={16}>
            <Col span={12}>
              {renderViewField("Departamento", participant?.departamento)}
            </Col>
            <Col span={12}>
              {renderViewField("Municipio", participant?.municipio)}
            </Col>
          </Row>
          <Divider orientation="left">Contacto y Estado</Divider>
          <Row gutter={16}>
            <Col span={12}>
              {renderViewField("Correo", participant?.correo)}
            </Col>
            <Col span={12}>
              {renderViewField(
                "Estado",
                participant?.estado_participante_nombre || participant?.estado_participante
              )}
            </Col>
          </Row>
          {participant?.codigo_unico && (
            <>
              <Divider orientation="left">Sistema</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  {renderViewField("Código Único", participant?.codigo_unico)}
                </Col>
                <Col span={12}>
                  {renderViewField(
                    "Fecha Creación",
                    participant?.created_at || participant?.created_at
                  )}
                </Col>
              </Row>
            </>
          )}

          <Row justify="end" style={{ marginTop: 24 }}>
            <Button onClick={onCancel}>Cerrar</Button>
          </Row>
        </div>
      ) : (
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          className="edit-form"
        >
          <Divider orientation="left">Información Personal</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Nombres"
                name="nombres"
                rules={[{ required: true, message: "Ingrese los nombres" }]}
              >
                <Input placeholder="Juan Carlos" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Apellidos"
                name="apellidos"
                rules={[{ required: true, message: "Ingrese los apellidos" }]}
              >
                <Input placeholder="Pérez Gómez" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Tipo Documento"
                name="tipo_doc_id"
                rules={[{ required: true, message: "Seleccione el tipo" }]}
              >
                <Select
                  placeholder="Seleccione"
                  loading={!maestros.tiposDocumento?.length}
                >
                  {maestros.tiposDocumento?.map((tipo) => (
                    <Option key={tipo.id} value={tipo.id}>
                      {tipo.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Número Identificación"
                name="num_identificacion"
                rules={[{ required: true, message: "Ingrese el número" }]}
              >
                <Input placeholder="12345678" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Género"
                name="genero_id"
                rules={[{ required: true, message: "Seleccione el género" }]}
              >
                <Select
                  placeholder="Seleccione"
                  loading={!maestros.generos?.length}
                >
                  {maestros.generos?.map((genero) => (
                    <Option key={genero.id} value={genero.id}>
                      {genero.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Fecha Nacimiento"
                name="fecha_nacimiento"
                rules={[{ required: true, message: "Seleccione la fecha" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  placeholder="Seleccione fecha"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Teléfono" name="telefono">
                <Input placeholder="3205554489" />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Ubicación</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Departamento"
                name="departamento_id"
                rules={[{ required: true, message: "Seleccione departamento" }]}
              >
                <Select
                  placeholder="Seleccione departamento"
                  onChange={handleDepartamentoChange}
                  loading={!maestros.departamentos?.length}
                >
                  {maestros.departamentos?.map((depto) => (
                    <Option key={depto.oid} value={depto.oid}>
                      {depto.departamento}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Municipio"
                name="municipio_id"
                rules={[{ required: true, message: "Seleccione municipio" }]}
              >
                <Select
                  placeholder="Seleccione municipio"
                  disabled={municipios.length === 0}
                  loading={loadingMunicipios}
                >
                  {municipios.map((mun) => (
                    <Option key={mun.oid} value={mun.oid}>
                      {mun.municipio}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Contacto y Estado</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Correo Electrónico" name="correo">
                <Input type="email" placeholder="usuario@email.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Estado"
                name="estado_id"
                rules={[{ required: true, message: "Seleccione estado" }]}
              >
                <Select
                  placeholder="Seleccione estado"
                  loading={!maestros.estadosParticipante?.length}
                >
                  {maestros.estadosParticipante?.map((estado) => (
                    <Option key={estado.id} value={estado.id}>
                      {estado.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" gutter={12} style={{ marginTop: 24 }}>
            <Col>
              <Button onClick={onCancel} disabled={loading}>
                Cancelar
              </Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                {isCreateMode ? "Crear Participante" : "Guardar Cambios"}
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
}